import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { useAuth } from "@/modules/auth/hooks/use-auth";
import { Button } from "@/modules/shared/ui/components/button";
import { IncreasePermission } from "../../constants/increase-permissions";
import { IncreasePopulation } from "../../enums/increase-population";
import { useCreateIncreasePeriod } from "../../hooks/useCreateIncreasePeriod";
import {
  OpenPeriodDialog,
  type OpenPeriodFormValues,
} from "../components/OpenPeriodDialog";

const REPORTING_LINES_ROUTE = "/salary-increases/reporting-lines";

export default function SalaryIncreasesPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canOpenPeriod = hasPermission(IncreasePermission.OpenPeriod);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Bumped after a successful submit so the dialog remounts with a blank form.
  const [dialogKey, setDialogKey] = useState(0);
  const createPeriod = useCreateIncreasePeriod();

  const handleSubmit = (values: OpenPeriodFormValues) => {
    createPeriod.mutate(
      {
        population: values.population,
        approvedPercentage: values.approvedPercentage,
        effectiveDate: values.effectiveDate,
        approvalRecord: values.approvedBy || undefined,
        companyCode: values.companyCode,
        companyRuleCodes: values.companyRuleCodes,
      },
      {
        onSuccess: (period) => {
          setDialogOpen(false);
          setDialogKey((key) => key + 1);
          // Wage periods are tracked only, so there is no reporting-line step.
          if (values.population === IncreasePopulation.Ctc) {
            navigate(`${REPORTING_LINES_ROUTE}?periodId=${period.id}`);
          }
        },
      },
    );
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Salary Increases</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of your salary increase periods, budget and progress.
        </p>
      </div>

      {canOpenPeriod && (
        <>
          <Button onClick={() => setDialogOpen(true)}>
            <PlayCircle className="mr-1.5 size-4" />
            Start New Period
          </Button>

          <OpenPeriodDialog
            key={dialogKey}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            isSubmitting={createPeriod.isPending}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </div>
  );
}
